import API_BASE from "./apiConfig";
import React, { useState, useRef } from "react";
import {
  FaUser, FaEnvelope, FaPhone, FaCalendar, FaTransgender,
  FaImage, FaUserFriends, FaUserPlus, FaSave, FaTimesCircle,
  FaUserEdit, FaSearch, FaRegClone, FaDownload, FaUpload,
  FaGraduationCap, FaCheckCircle, FaExclamationCircle, FaChild,
  FaBaseballBall, FaIdCard, FaBirthdayCake, FaShieldAlt,
} from "react-icons/fa";
import ParentAccessPanel from "./ParentAccessPanel";
import { Colors, Shadows, Radii } from "./branding";
import { useTheme, getT } from "./ThemeContext";
const BASE = API_BASE;
// ─── Static design tokens (non-theme-varying) ─────────────────────────────────
const ST = {
  blur:        "blur(20px)",
  cardRadius:  "16px",
  inputRadius: "10px",
  yellow:      Colors.logoYellow || "#f7ce0b",
  blue:        Colors.primaryBlue || "#034e7b",
  blueDark:    "#02304f",
  font:        "'Segoe UI', system-ui, sans-serif",
};

// Age category colour palette
const AGE_CAT_COLORS = {
  U9:    { bg: "rgba(251,191,36,0.12)",  border: "#fbbf24", text: "#fbbf24" },
  U11:   { bg: "rgba(52,211,153,0.12)",  border: "#34d399", text: "#34d399" },
  U13:   { bg: "rgba(96,165,250,0.12)",  border: "#60a5fa", text: "#60a5fa" },
  U15:   { bg: "rgba(167,139,250,0.12)", border: "#a78bfa", text: "#a78bfa" },
  U17:   { bg: "rgba(244,114,182,0.12)", border: "#f472b6", text: "#f472b6" },
  Adult: { bg: "rgba(148,163,184,0.12)", border: "#94a3b8", text: "#94a3b8" },
};

// ─── Shared styles (theme-aware functions) ────────────────────────────────────
const labelSt = (T) => ({
  display: "block", marginBottom: "7px", fontSize: "11px", fontWeight: 700,
  letterSpacing: "0.06em", textTransform: "uppercase", color: T.textLabel,
  fontFamily: ST.font,
});
const inputWrapSt = (T) => ({
  display: "flex", alignItems: "center", border: `1.5px solid ${T.inputBdr}`,
  borderRadius: ST.inputRadius, padding: "10px 14px", background: T.inputBg,
  backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
  width: "100%", boxSizing: "border-box",
});
const inputSt = (T) => ({
  border: "none", outline: "none", width: "100%", background: "transparent",
  fontSize: "14px", color: T.inputText, fontFamily: ST.font,
});
const lightText = (T) => ({
  color: T.textMuted, fontWeight: 500, marginLeft: "4px",
  textTransform: "none", letterSpacing: 0,
});
const glassCard = (T) => ({
  background: T.glass, backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
  border: T.glassBdr, borderRadius: ST.cardRadius, boxShadow: T.glassSh,
  padding: "32px 36px", boxSizing: "border-box",
});
const yellowBtn = {
  padding: "11px 22px", border: "none", borderRadius: "10px", fontSize: "14px",
  fontWeight: 800, cursor: "pointer", WebkitTapHighlightColor:"transparent",
  color: "#02304f", display: "flex",
  alignItems: "center", justifyContent: "center", gap: "8px",
  backgroundImage: "linear-gradient(135deg, #f7ce0b, #e8b800)",
  boxShadow: "0 4px 20px rgba(247,206,11,0.35)",
};
const blueBtn = (T) => ({
  padding: "11px 22px", border: `1px solid ${T.inputBdr}`, borderRadius: "10px",
  fontSize: "14px", fontWeight: 700, cursor: "pointer", WebkitTapHighlightColor:"transparent",
  color: T.inputText,
  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
  background: T.divider, backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
  boxShadow: "0 4px 14px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)",
});
const toastSt = (ok) => ({
  display: "flex", alignItems: "center", gap: "10px", padding: "13px 18px",
  borderRadius: "10px", marginBottom: "20px", fontSize: "13px", fontWeight: 600,
  background: ok ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)",
  color: ok ? "#34d399" : "#f87171",
  border: ok ? "1px solid rgba(52,211,153,0.30)" : "1px solid rgba(248,113,113,0.30)",
  backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
});
const tabBtnSt = (active, T) => ({
  padding: "10px 22px", borderRadius: "10px", fontSize: "13px", fontWeight: 700,
  cursor: "pointer", WebkitTapHighlightColor:"transparent", display: "flex", alignItems: "center", gap: "8px",
  letterSpacing: "0.02em",
  color: active ? "#02304f" : T.textLabel,
  background: active ? "linear-gradient(135deg, #f7ce0b, #e8b800)" : T.glass,
  backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
  border: active ? "1px solid rgba(255,200,0,0.4)" : T.glassBdr,
  boxShadow: active ? "0 4px 14px rgba(255,200,0,0.30)" : T.glassSh,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

/** Client-side mirror of backend compute_age_category. DOB is "YYYY-MM-DD". */
function computeAgeCategory(dobStr) {
  if (!dobStr) return null;
  const dob   = new Date(dobStr + "T00:00:00");
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  if (
    today.getMonth() < dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
  ) age--;
  if (age < 9)  return "U9";
  if (age < 11) return "U11";
  if (age < 13) return "U13";
  if (age < 15) return "U15";
  if (age < 17) return "U17";
  return "Adult";
}

const blankForm = (today) => ({
  first_name: "", last_name: "", date_of_birth: "", gender: "",
  parent_name: "", parent_email: "", parent_phone: "",
  join_date: today, active_status: 1, skill_level_id: "",
  playing_role_id: "", cricclub_player_id: "",
  photo_file: null, existing_photo_url: "",
});

// ─── Age Category Badge ───────────────────────────────────────────────────────
function AgeCategoryBadge({ code, large = false }) {
  if (!code) return null;
  const c = AGE_CAT_COLORS[code] || AGE_CAT_COLORS["Adult"];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: large ? 6 : 4,
      padding: large ? "5px 14px" : "3px 10px",
      borderRadius: 20, fontSize: large ? 13 : 11, fontWeight: 800,
      background: c.bg, border: `1.5px solid ${c.border}`, color: c.text,
      letterSpacing: "0.03em",
    }}>
      <FaBirthdayCake size={large ? 12 : 9} /> {code}
    </span>
  );
}

// ─── Section divider ──────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: "10px", margin: "6px 0 0" }}>
      <div style={{ height: "2px", width: "18px", background: ST.yellow, borderRadius: "2px" }} />
      <span style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", color: "#8899aa" }}>
        {children}
      </span>
      <div style={{ flex: 1, height: "1px", background: "rgba(0,53,128,0.08)" }} />
    </div>
  );
}

// ─── Reusable fields ─────────────────────────────────────────────────────────
function Field({ label, icon, name, type = "text", value, onChange, maxLength, error, placeholder, readOnly }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div>
      <label style={labelSt(T)}>{label}</label>
      <div style={{
        ...inputWrapSt(T),
        border: error ? "1.5px solid #c0392b" : "1.5px solid rgba(0,53,128,0.13)",
        ...(readOnly ? { background: "rgba(240,244,255,0.7)" } : {}),
      }}>
        {icon && React.cloneElement(icon, { style: { marginRight: "10px", color: T.textMuted, flexShrink: 0 } })}
        <input type={type} name={name} value={value} onChange={onChange}
          style={{ ...inputSt(T), ...(readOnly ? { color: "#6a7a8a", cursor: "default" } : {}) }}
          maxLength={maxLength} placeholder={placeholder} readOnly={readOnly} />
      </div>
      {error && (
        <p style={{ color: "#c0392b", fontSize: "11px", margin: "5px 0 0", display: "flex", alignItems: "center", gap: "4px" }}>
          <FaExclamationCircle size={10} /> {error}
        </p>
      )}
    </div>
  );
}

function SelectField({ label, icon, name, value, onChange, options, error }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div>
      <label style={labelSt(T)}>{label}</label>
      <div style={{
        ...inputWrapSt(T),
        border: error ? "1.5px solid #c0392b" : "1.5px solid rgba(0,53,128,0.13)",
      }}>
        {icon && React.cloneElement(icon, { style: { marginRight: "10px", color: T.textMuted, flexShrink: 0 } })}
        <select name={name} value={value} onChange={onChange} style={{ ...inputSt(T), cursor: "pointer", WebkitTapHighlightColor:"transparent", colorScheme: isDark ? "dark" : "light" }}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {error && (
        <p style={{ color: "#c0392b", fontSize: "11px", margin: "5px 0 0", display: "flex", alignItems: "center", gap: "4px" }}>
          <FaExclamationCircle size={10} /> {error}
        </p>
      )}
    </div>
  );
}

/** Grouped playing-role <select> with <optgroup> per role_group */
function PlayingRoleSelect({ name, value, onChange, rolesByGroup }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div>
      <label style={labelSt(T)}>Playing Role</label>
      <div style={inputWrapSt(T)}>
        <FaBaseballBall style={{ marginRight: 10, color: T.textMuted, flexShrink: 0 }} />
        <select name={name} value={value} onChange={onChange} style={{ ...inputSt(T), cursor: "pointer", WebkitTapHighlightColor:"transparent", colorScheme: isDark ? "dark" : "light" }}>
          <option value="">Select playing role</option>
          {Object.entries(rolesByGroup).map(([grp, roles]) => (
            <optgroup key={grp} label={grp}>
              {roles.map(r => <option key={r.role_id} value={String(r.role_id)}>{r.role_name}</option>)}
            </optgroup>
          ))}
        </select>
      </div>
    </div>
  );
}

/** Age Category read-only display field */
function AgeCategoryField({ dobStr }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const code = computeAgeCategory(dobStr);
  return (
    <div>
      <label style={labelSt(T)}>
        Age Category <span style={{ ...lightText(T), fontSize: 10 }}>(auto from DOB)</span>
      </label>
      <div style={{ ...inputWrapSt(T), background: "rgba(240,244,255,0.7)", gap: 10 }}>
        <FaBirthdayCake style={{ color: T.textMuted, flexShrink: 0 }} />
        {code
          ? <AgeCategoryBadge code={code} />
          : <span style={{ color: "#aab", fontSize: 13 }}>Enter date of birth above</span>
        }
      </div>
    </div>
  );
}

// ─── Student Info Header Card ─────────────────────────────────────────────────
function StudentInfoHeader({ student }) {
  if (!student) return null;
  const ageCode     = computeAgeCategory(student.date_of_birth);
  const statusLabel = student.active_status === 1 ? "Active"
                    : student.active_status === 2 ? "Suspended" : "Cancelled";
  const statusColor = student.active_status === 1 ? "#059669"
                    : student.active_status === 2 ? "#d97706" : "#dc2626";
  const statusBg    = student.active_status === 1 ? "rgba(5,150,105,0.08)"
                    : student.active_status === 2 ? "rgba(217,119,6,0.08)"  : "rgba(220,38,38,0.08)";

  return (
    <div style={{
      background: "linear-gradient(135deg,rgba(3,78,123,0.06),rgba(247,206,11,0.06))",
      border: "1.5px solid rgba(3,78,123,0.14)", borderRadius: 14,
      padding: "16px 20px", marginBottom: 24,
      display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
    }}>
      {/* Avatar */}
      <div style={{
        width: 54, height: 54, borderRadius: "50%", flexShrink: 0,
        background: `linear-gradient(135deg,${ST.yellow},#fff3a0)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 3px 10px rgba(255,200,0,0.28)", overflow: "hidden",
      }}>
        {student.photo_url
          ? <img src={`${BASE}/${student.photo_url}`} alt="photo"
              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <FaUser size={22} color={ST.blue} />
        }
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 200 }}>
        {/* Row 1 — name + badges */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 7 }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: ST.blue }}>
            {student.first_name} {student.last_name}
          </span>
          {ageCode && <AgeCategoryBadge code={ageCode} large />}
          <span style={{
            display: "inline-flex", alignItems: "center",
            padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: statusBg, color: statusColor, border: `1.5px solid ${statusColor}44`,
          }}>{statusLabel}</span>
        </div>
        {/* Row 2 — meta chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px 18px", fontSize: 12, color: "#6a7a8a" }}>
          {student.student_code && (
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <FaIdCard size={11} /> ID: <strong style={{ color: ST.blue }}>{student.student_code}</strong>
            </span>
          )}
          {student.gender && (
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <FaTransgender size={11} /> {student.gender}
            </span>
          )}
          {student.date_of_birth && (
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <FaBirthdayCake size={11} />
              {new Date(student.date_of_birth + "T00:00:00").toLocaleDateString("en-US")}
            </span>
          )}
          {student.level_name && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
              color: student.color_hex || ST.blue,
              background: (student.color_hex || ST.blue) + "18",
              border: `1px solid ${(student.color_hex || ST.blue)}44`,
            }}>
              <FaGraduationCap size={9} /> {student.level_name}
            </span>
          )}
          {student.role_name && (
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <FaBaseballBall size={10} /> {student.role_name}
            </span>
          )}
          {student.cricclub_player_id && (
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <FaShieldAlt size={10} /> CricClub:&nbsp;
              <strong style={{ color: ST.blue }}>{student.cricclub_player_id}</strong>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CREATE PANEL ─────────────────────────────────────────────────────────────
function CreatePanel({ currentUserId }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const today   = new Date().toISOString().split("T")[0];
  const fileRef = useRef(null);
  const [form,         setForm]         = useState(blankForm(today));
  const [errors,       setErrors]       = useState({});
  const [msg,          setMsg]          = useState(null);
  const [skillLevels,  setSkillLevels]  = useState([]);
  const [playingRoles, setPlayingRoles] = useState([]);

  React.useEffect(() => {
    fetch(`${BASE}/students/skill-levels`)
      .then(r => r.json()).then(d => setSkillLevels(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/students/playing-roles`)
      .then(r => r.json()).then(d => setPlayingRoles(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const rolesByGroup = playingRoles.reduce((acc, r) => {
    (acc[r.role_group] = acc[r.role_group] || []).push(r);
    return acc;
  }, {});

  const validate = (name, value) => {
    let e = "";
    if (name === "parent_email" && value && !value.includes("@")) e = "Email must contain @";
    if (name === "parent_phone" && value.replace(/\D/g, "").length !== 10) e = "Phone must be 10 digits";
    if (name === "cricclub_player_id" && value.length > 15) e = "Max 15 characters";
    setErrors((p) => ({ ...p, [name]: e }));
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "photo_file") { setForm({ ...form, photo_file: files?.[0] || null }); return; }
    if (name === "parent_phone") { const f = formatPhone(value); validate(name, f); setForm({ ...form, parent_phone: f }); return; }
    validate(name, value);
    setForm({ ...form, [name]: value });
  };

  const clear = () => {
    setForm(blankForm(today)); setErrors({});
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.first_name.trim())  newErrors.first_name    = "First name is required";
    if (!form.last_name.trim())   newErrors.last_name     = "Last name is required";
    if (!form.date_of_birth)      newErrors.date_of_birth = "Date of birth is required";
    if (!form.gender)             newErrors.gender        = "Gender is required";
    if (!form.join_date)          newErrors.join_date     = "Join date is required";
    if (!form.parent_name.trim()) newErrors.parent_name   = "Parent name is required";
    if (!form.parent_email || !form.parent_email.includes("@")) newErrors.parent_email = "Valid email required";
    if (form.parent_phone.replace(/\D/g, "").length !== 10) newErrors.parent_phone = "Phone must be 10 digits";
    if (form.cricclub_player_id && form.cricclub_player_id.length > 15) newErrors.cricclub_player_id = "Max 15 characters";
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

    const payload = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === "photo_file") return;
      if (k === "parent_phone") payload.append(k, v.replace(/\D/g, ""));
      else payload.append(k, v ?? "");
    });
    payload.append("updated_by", currentUserId ? String(currentUserId) : "admin");
    if (form.photo_file) payload.append("photo_file", form.photo_file);
    try {
      const res  = await fetch(`${BASE}/students/`, { method: "POST", body: payload });
      const data = await res.json();
      if (data.success) { setMsg({ ok: true, text: "Student created successfully!" }); clear(); }
      else setMsg({ ok: false, text: data.message || "Error saving student" });
    } catch { setMsg({ ok: false, text: "Network error while saving student" }); }
  };

  return (
    <div>
      {msg && <div style={toastSt(msg.ok)}>{msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}</div>}
      <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px 28px" }}>
        <SectionLabel>Student Info</SectionLabel>

        <Field label="First Name" icon={<FaUser />} name="first_name"
          value={form.first_name} onChange={handleChange} placeholder="First name" error={errors.first_name} />
        <Field label="Last Name" icon={<FaUser />} name="last_name"
          value={form.last_name} onChange={handleChange} placeholder="Last name" error={errors.last_name} />

        <Field label={<>Date of Birth <span style={lightText(T)}>(mm/dd/yyyy)</span></>}
          icon={<FaCalendar />} name="date_of_birth" type="date"
          value={form.date_of_birth} onChange={handleChange} error={errors.date_of_birth} />
        <SelectField label="Gender" icon={<FaTransgender />} name="gender"
          value={form.gender} onChange={handleChange}
          options={[["", "Select gender"], ["Male", "Male"], ["Female", "Female"], ["Other", "Other"]]}
          error={errors.gender} />

        <Field label={<>Join Date <span style={lightText(T)}>(mm/dd/yyyy)</span></>}
          icon={<FaCalendar />} name="join_date" type="date"
          value={form.join_date} onChange={handleChange} error={errors.join_date} />
        <SelectField label="Skill Level" icon={<FaGraduationCap />} name="skill_level_id"
          value={form.skill_level_id} onChange={handleChange}
          options={[["", "Select skill level"], ...skillLevels.map(l => [String(l.level_id), l.level_name])]} />

        {/* Age Category — auto from DOB */}
        <AgeCategoryField dobStr={form.date_of_birth} />

        {/* Playing Role — grouped dropdown */}
        <PlayingRoleSelect name="playing_role_id" value={form.playing_role_id}
          onChange={handleChange} rolesByGroup={rolesByGroup} />

        {/* CricClub Player ID */}
        <Field label={<>CricClub Player ID <span style={lightText(T)}>(max 15 chars)</span></>}
          icon={<FaShieldAlt />} name="cricclub_player_id"
          value={form.cricclub_player_id} onChange={handleChange}
          maxLength={15} placeholder="e.g. CC1234567890123"
          error={errors.cricclub_player_id} />

        {/* Active status */}
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <label style={labelSt(T)}>Active Status</label>
          <div style={{ ...inputWrapSt(T), gap: "10px" }}>
            <input type="checkbox" checked readOnly style={{ accentColor: ST.blue, width: "16px", height: "16px" }} />
            <span style={{ fontSize: "14px", color: T.textSub }}>Active — always enabled on create</span>
          </div>
        </div>

        <SectionLabel>Parent / Guardian</SectionLabel>
        <Field label={<>Parent Name <span style={lightText(T)}>(First & Last)</span></>}
          icon={<FaUserFriends />} name="parent_name"
          value={form.parent_name} onChange={handleChange} placeholder="Full name" error={errors.parent_name} />
        <Field label="Parent Email" icon={<FaEnvelope />} name="parent_email"
          value={form.parent_email} onChange={handleChange}
          error={errors.parent_email} placeholder="email@example.com" />
        <Field label={<>Phone <span style={lightText(T)}>(xxx-xxx-xxxx)</span></>}
          icon={<FaPhone />} name="parent_phone"
          value={form.parent_phone} onChange={handleChange}
          maxLength={12} error={errors.parent_phone} placeholder="000-000-0000" />
        <div>
          <label style={labelSt(T)}>Upload Photo</label>
          <div style={inputWrapSt(T)}>
            <FaImage style={{ marginRight: "10px", color: T.textMuted, flexShrink: 0 }} />
            <input type="file" name="photo_file" accept="image/*"
              onChange={handleChange} style={{ fontSize: "13px" }} ref={fileRef} />
          </div>
        </div>

        <div style={{ gridColumn: "1 / -1", display: "flex", gap: "14px", marginTop: "10px" }}>
          <button type="submit" style={{ ...yellowBtn, flex: 1, padding: "13px" }}><FaSave size={15} /> Save Student</button>
          <button type="button" onClick={clear} style={{ ...blueBtn(T), flex: 1, padding: "13px" }}><FaTimesCircle size={15} /> Clear</button>
        </div>
      </form>
    </div>
  );
}

// ─── UPDATE PANEL ─────────────────────────────────────────────────────────────
function UpdatePanel({ currentUserId }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const today   = new Date().toISOString().split("T")[0];
  const fileRef = useRef(null);
  const [search,       setSearch]       = useState("");
  const [results,      setResults]      = useState([]);
  const [selectedId,   setSelectedId]   = useState(null);
  const [studentInfo,  setStudentInfo]  = useState(null);
  const [form,         setForm]         = useState(blankForm(today));
  const [errors,       setErrors]       = useState({});
  const [msg,          setMsg]          = useState(null);
  const [skillLevels,  setSkillLevels]  = useState([]);
  const [playingRoles, setPlayingRoles] = useState([]);

  React.useEffect(() => {
    fetch(`${BASE}/students/skill-levels`)
      .then(r => r.json()).then(d => setSkillLevels(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/students/playing-roles`)
      .then(r => r.json()).then(d => setPlayingRoles(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const rolesByGroup = playingRoles.reduce((acc, r) => {
    (acc[r.role_group] = acc[r.role_group] || []).push(r);
    return acc;
  }, {});

  const validate = (name, value) => {
    let e = "";
    if (name === "parent_email" && value && !value.includes("@")) e = "Email must contain @";
    if (name === "parent_phone" && value.replace(/\D/g, "").length !== 10) e = "Phone must be 10 digits";
    if (name === "cricclub_player_id" && value.length > 15) e = "Max 15 characters";
    setErrors((p) => ({ ...p, [name]: e }));
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "photo_file") { setForm({ ...form, photo_file: files?.[0] || null }); return; }
    if (name === "parent_phone") { const f = formatPhone(value); validate(name, f); setForm({ ...form, parent_phone: f }); return; }
    validate(name, value);
    setForm({ ...form, [name]: value });
    // Keep header card DOB in sync for live badge update
    if (name === "date_of_birth") {
      setStudentInfo(prev => prev ? { ...prev, date_of_birth: value } : prev);
    }
  };

  const doSearch = async (val) => {
    setSearch(val);
    if (val.trim().length < 2) { setResults([]); return; }
    try {
      const res  = await fetch(`${BASE}/students/search?name=${val}`);
      const data = await res.json();
      setResults((data.students || []).filter((s) => s.active_status === 1));
    } catch { setResults([]); }
  };

  const loadStudent = async (id, label) => {
    setSelectedId(id); setSearch(label); setResults([]);
    const res  = await fetch(`${BASE}/students/${id}`);
    const data = await res.json();
    const s    = data.student;
    setStudentInfo(s);
    setForm({
      first_name:         s.first_name,
      last_name:          s.last_name,
      date_of_birth:      s.date_of_birth,
      gender:             s.gender,
      parent_name:        s.parent_name,
      parent_email:       s.parent_email,
      parent_phone:       formatPhone(s.parent_phone),
      join_date:          s.join_date,
      active_status:      s.active_status,
      skill_level_id:     s.skill_level_id  ? String(s.skill_level_id)  : "",
      playing_role_id:    s.playing_role_id ? String(s.playing_role_id) : "",
      cricclub_player_id: s.cricclub_player_id || "",
      existing_photo_url: s.photo_url || "",
      photo_file: null,
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (form.cricclub_player_id && form.cricclub_player_id.length > 15) {
      setErrors(p => ({ ...p, cricclub_player_id: "Max 15 characters" })); return;
    }
    const payload = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === "photo_file") return;
      if (k === "parent_phone") payload.append(k, v.replace(/\D/g, ""));
      else payload.append(k, v ?? "");
    });
    payload.append("updated_by", currentUserId || "admin");
    if (form.photo_file) payload.append("photo_file", form.photo_file);
    try {
      const res  = await fetch(`${BASE}/students/${selectedId}`, { method: "PUT", body: payload });
      const data = await res.json();
      if (data.success) {
        setMsg({ ok: true, text: "Student updated successfully!" });
        // Refresh header card
        const r2 = await fetch(`${BASE}/students/${selectedId}`);
        const d2 = await r2.json();
        if (d2.student) setStudentInfo(d2.student);
      } else {
        setMsg({ ok: false, text: data.message || "Error updating student" });
      }
    } catch { setMsg({ ok: false, text: "Network error while updating student" }); }
  };

  return (
    <div>
      {msg && <div style={toastSt(msg.ok)}>{msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}</div>}

      {/* Search box */}
      <div style={{ marginBottom: "24px", position: "relative" }}>
        <label style={labelSt(T)}>Search Student</label>
        <div style={{ ...inputWrapSt(T), background: "rgba(255,255,255,0.90)" }}>
          <FaSearch style={{ marginRight: "10px", color: T.textMuted }} />
          <input type="text" value={search} onChange={(e) => doSearch(e.target.value)}
            placeholder="Type student name…" style={inputSt(T)} />
        </div>
        {results.length > 0 && (
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, width: "100%",
            background: "rgba(255,255,255,0.96)", backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
            border: T.glassBdr, borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,40,100,0.14)", zIndex: 20,
            maxHeight: "200px", overflowY: "auto", WebkitOverflowScrolling:"touch",
          }}>
            {results.map((s, i) => (
              <div key={s.student_id}
                onClick={() => loadStudent(s.student_id, `${s.first_name} ${s.last_name}`)}
                style={{
                  padding: "11px 16px", cursor: "pointer", WebkitTapHighlightColor:"transparent", fontSize: "14px",
                  borderBottom: i < results.length - 1 ? "1px solid rgba(0,53,128,0.07)" : "none",
                  color: "#1a2840", fontWeight: 500,
                  display: "flex", alignItems: "center", gap: 8,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,200,0,0.14)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                <span>{s.first_name} {s.last_name}</span>
                {s.age_category_code && <AgeCategoryBadge code={s.age_category_code} />}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Student info header card */}
      {selectedId && studentInfo && <StudentInfoHeader student={studentInfo} />}

      {/* Edit form */}
      {selectedId && (
        <form onSubmit={handleUpdate} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px 28px" }}>
          <SectionLabel>Student Info</SectionLabel>

          <Field label="First Name" icon={<FaUser />} name="first_name" value={form.first_name} onChange={handleChange} />
          <Field label="Last Name"  icon={<FaUser />} name="last_name"  value={form.last_name}  onChange={handleChange} />

          <Field label={<>Date of Birth <span style={lightText(T)}>(mm/dd/yyyy)</span></>}
            icon={<FaCalendar />} name="date_of_birth" type="date"
            value={form.date_of_birth} onChange={handleChange} />
          <SelectField label="Gender" icon={<FaTransgender />} name="gender"
            value={form.gender} onChange={handleChange}
            options={[["", "Select gender"], ["Male", "Male"], ["Female", "Female"], ["Other", "Other"]]} />

          <Field label={<>Join Date <span style={lightText(T)}>(mm/dd/yyyy)</span></>}
            icon={<FaCalendar />} name="join_date" type="date"
            value={form.join_date} onChange={handleChange} />
          <SelectField label="Skill Level" icon={<FaGraduationCap />} name="skill_level_id"
            value={form.skill_level_id} onChange={handleChange}
            options={[["", "Select skill level"], ...skillLevels.map(l => [String(l.level_id), l.level_name])]} />

          {/* Age Category — auto from DOB */}
          <AgeCategoryField dobStr={form.date_of_birth} />

          {/* Playing Role — grouped */}
          <PlayingRoleSelect name="playing_role_id" value={form.playing_role_id}
            onChange={handleChange} rolesByGroup={rolesByGroup} />

          {/* CricClub Player ID */}
          <Field label={<>CricClub Player ID <span style={lightText(T)}>(max 15 chars)</span></>}
            icon={<FaShieldAlt />} name="cricclub_player_id"
            value={form.cricclub_player_id} onChange={handleChange}
            maxLength={15} placeholder="e.g. CC1234567890123"
            error={errors.cricclub_player_id} />

          <SelectField label="Active Status" name="active_status" value={form.active_status}
            onChange={handleChange} options={[[1, "Active"], [2, "Suspended"], [3, "Deleted"]]} />

          <SectionLabel>Parent / Guardian</SectionLabel>
          <Field label={<>Parent Name <span style={lightText(T)}>(First & Last)</span></>}
            icon={<FaUserFriends />} name="parent_name" value={form.parent_name} onChange={handleChange} />
          <Field label="Parent Email" icon={<FaEnvelope />} name="parent_email"
            value={form.parent_email} onChange={handleChange} error={errors.parent_email} />
          <Field label={<>Phone <span style={lightText(T)}>(xxx-xxx-xxxx)</span></>}
            icon={<FaPhone />} name="parent_phone"
            value={form.parent_phone} onChange={handleChange} maxLength={12} error={errors.parent_phone} />
          <div>
            <label style={labelSt(T)}>Upload New Photo (optional)</label>
            <div style={inputWrapSt(T)}>
              <FaImage style={{ marginRight: "10px", color: T.textMuted, flexShrink: 0 }} />
              <input type="file" name="photo_file" accept="image/*"
                onChange={handleChange} style={{ fontSize: "13px" }} ref={fileRef} />
            </div>
          </div>

          <div style={{ gridColumn: "1 / -1", marginTop: "10px" }}>
            <button type="submit" style={{ ...yellowBtn, width: "100%", padding: "13px" }}>
              <FaSave size={15} /> Update Student
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── BULK PANEL ───────────────────────────────────────────────────────────────
function BulkPanel() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const fileRef = useRef(null);
  const [file,     setFile]     = useState(null);
  const [report,   setReport]   = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading,  setLoading]  = useState(false);

  const handleDownload = () => window.open(`${BASE}/students/bulk/template`);

  const handleUpload = async () => {
    setErrorMsg(""); setReport(null);
    if (!file) { setErrorMsg("Please select an Excel file first."); return; }
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    try {
      const res  = await fetch(`${BASE}/students/bulk/upload`, { method: "POST", body: formData });
      const data = await res.json();
      if (fileRef.current) fileRef.current.value = "";
      setFile(null);
      if (data.error) { setErrorMsg(data.error); setReport(null); }
      else if (data.rows?.length > 0) setReport(data);
      else setErrorMsg("No data was imported. Please contact the System Administrator.");
    } catch {
      setErrorMsg("System error. Please contact the System Administrator.");
    } finally { setLoading(false); }
  };

  const friendlyHeader = (k) => {
    const map = {
      Status: "Status", Grade: "Active Status", active_status: "Active Status",
      "Update Start Time": "Created On", "Update End Time": "Updated On",
      student_id: "Student ID", ID: "ID", first_name: "First Name", last_name: "Last Name",
      date_of_birth: "DOB", "Age Cat.": "Age Cat.", gender: "Gender",
      parent_name: "Parent", parent_email: "Email", parent_phone: "Phone",
      join_date: "Joined", updated_by: "Updated By",
    };
    return map[k] || k.replace(/_/g, " ");
  };

  const transformValue = (k, v) => {
    if (["active_status", "Grade", "grade"].includes(k)) {
      if (v === 1 || v === "1") return "Active";
      if (v === 2 || v === "2") return "Suspended";
      if (v === 3 || v === "3") return "Cancelled";
    }
    return v;
  };

  const statusPill = (s) => {
    const str = String(s || "");
    if (str === "Updated")  return { color: "#1a6e34", background: "rgba(39,174,96,0.12)",  border: "1px solid rgba(39,174,96,0.25)"  };
    if (str === "Inserted") return { color: "#0a4a8a", background: "rgba(0,91,187,0.10)",   border: "1px solid rgba(0,91,187,0.22)"   };
    if (str.startsWith("Error")) return { color: "#b30000", background: "rgba(220,0,0,0.08)", border: "1px solid rgba(220,0,0,0.20)" };
    return {};
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "20px", marginBottom: "28px", flexWrap: "wrap" }}>
        {/* Download */}
        <div style={{
          flex: "1 1 220px", background: T.glass, backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
          border: T.glassBdr, borderRadius: "14px", boxShadow: T.glassSh,
          padding: "20px 24px", display: "flex", flexDirection: "column", gap: "14px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              width: "34px", height: "34px", borderRadius: "9px", flexShrink: 0,
              background: `linear-gradient(135deg, ${ST.yellow}, #fff3a0)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 3px 10px rgba(255,200,0,0.28)",
            }}><FaDownload size={14} color={ST.blue} /></div>
            <div>
              <div style={{ fontSize: "13px", fontWeight: 800, color: ST.blue }}>Download Template</div>
              <div style={{ fontSize: "11px", color: "#8899aa" }}>Get the Excel template</div>
            </div>
          </div>
          <button onClick={handleDownload} style={{ ...yellowBtn, width: "100%" }}>
            <FaDownload size={13} /> Download
          </button>
        </div>

        {/* Upload */}
        <div style={{
          flex: "2 1 320px", background: T.glass, backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
          border: T.glassBdr, borderRadius: "14px", boxShadow: T.glassSh,
          padding: "20px 24px", display: "flex", flexDirection: "column", gap: "14px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              width: "34px", height: "34px", borderRadius: "9px", flexShrink: 0,
              background: "rgba(0,31,91,0.72)", backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
              border: "1px solid rgba(255,255,255,0.20)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 3px 10px rgba(0,31,91,0.22)",
            }}><FaUpload size={14} color="white" /></div>
            <div>
              <div style={{ fontSize: "13px", fontWeight: 800, color: ST.blue }}>Upload Data</div>
              <div style={{ fontSize: "11px", color: "#8899aa" }}>Bulk import / update students via Excel</div>
            </div>
          </div>
          <div style={{ ...inputWrapSt(T), gap: "8px" }}>
            <FaUpload style={{ color: T.textMuted, flexShrink: 0 }} />
            <input ref={fileRef} type="file" accept=".xlsx"
              onChange={(e) => setFile(e.target.files[0])} style={{ fontSize: "13px", flex: 1 }} />
          </div>
          <button onClick={handleUpload} disabled={loading}
            style={{ ...blueBtn(T), width: "100%", opacity: loading ? 0.7 : 1 }}>
            <FaUpload size={13} /> {loading ? "Uploading…" : "Upload Data"}
          </button>
        </div>
      </div>

      {errorMsg && <div style={toastSt(false)}><FaExclamationCircle /> {errorMsg}</div>}

      {report?.rows?.length > 0 && (
        <div>
          <div style={{
            fontSize: "11px", fontWeight: 800, letterSpacing: "0.10em",
            textTransform: "uppercase", color: ST.blue, marginBottom: "12px",
            display: "flex", alignItems: "center", gap: "8px",
          }}>
            <div style={{ width: "3px", height: "16px", background: ST.yellow, borderRadius: "2px" }} />
            Bulk Update Report — {report.rows.length} row{report.rows.length !== 1 ? "s" : ""}
          </div>
          <div style={{
            background: T.glass, backdropFilter: ST.blur, WebkitBackdropFilter: ST.blur,
            border: T.glassBdr, borderRadius: "14px", boxShadow: T.glassSh,
            overflow: "hidden", width: "100%",
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ background: "rgba(0,53,128,0.07)" }}>
                  {Object.keys(report.rows[0]).map((k) => (
                    <th key={k} style={{
                      padding: "10px", textAlign: "left", fontWeight: 800, color: ST.blue,
                      fontSize: "10px", letterSpacing: "0.07em", textTransform: "uppercase",
                      borderBottom: "1.5px solid rgba(0,53,128,0.10)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{friendlyHeader(k)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row, idx) => (
                  <tr key={idx}
                    style={{ background: idx % 2 === 0 ? T.rowEven : T.rowOdd }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,200,0,0.08)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? T.rowEven : T.rowOdd)}>
                    {Object.keys(row).map((k) => {
                      const v       = transformValue(k, row[k]);
                      const display = v === null || v === undefined ? "" : String(v);
                      const isStatus = k === "Status";
                      const isAge    = k === "Age Cat.";
                      return (
                        <td key={k} style={{
                          padding: "8px 10px", borderBottom: "1px solid rgba(0,53,128,0.06)",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: T.textSub,
                        }}>
                          {isStatus ? (
                            <span style={{ ...statusPill(display), borderRadius: "6px", padding: "2px 9px", fontWeight: 700, fontSize: "11px", display: "inline-block" }}>
                              {display}
                            </span>
                          ) : isAge ? (
                            <AgeCategoryBadge code={display} />
                          ) : display}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
const StudentMaster = ({ currentUserId }) => {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [activeTab, setActiveTab] = useState("create");
  const tabs = [
    { key: "create",       label: "Create Student",  icon: <FaUserPlus size={14} /> },
    { key: "update",       label: "Update Student",  icon: <FaUserEdit size={14} /> },
    { key: "bulk",         label: "Bulk Update",     icon: <FaRegClone size={14} /> },
    { key: "parentaccess", label: "Parent Access",   icon: <FaChild    size={14} /> },
  ];

  return (
    <div style={{ minHeight: "100%", padding: "20px 28px 48px", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "20px" }}>
        <div style={{
          width: "42px", height: "42px", borderRadius: "12px", flexShrink: 0,
          background: `linear-gradient(135deg, ${ST.yellow}, #fff3a0)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 14px rgba(255,200,0,0.32)",
        }}>
          <FaGraduationCap size={20} color={ST.blue} />
        </div>
        <div>
          <h2 style={{ fontSize: "22px", fontWeight: 800, color: ST.blue, margin: 0, letterSpacing: "-0.01em" }}>
            Student Master
          </h2>
          <p style={{ margin: 0, fontSize: "12px", color: "#8899aa", marginTop: "1px" }}>
            Create, update and bulk-manage student records
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={tabBtnSt(activeTab === t.key, T)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div style={{
        ...glassCard(T),
        maxWidth: (activeTab === "bulk" || activeTab === "parentaccess") ? "100%" : "860px",
        margin: "0 auto", transition: "max-width 0.3s",
      }}>
        {activeTab === "create"       && <CreatePanel currentUserId={currentUserId} />}
        {activeTab === "update"       && <UpdatePanel currentUserId={currentUserId} />}
        {activeTab === "bulk"         && <BulkPanel />}
        {activeTab === "parentaccess" && <ParentAccessPanel currentUserId={currentUserId} />}
      </div>
    </div>
  );
};

export default StudentMaster;
